import { AxiosError } from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { axiosInstance } from "@/shared/lib/axiosInstance";
import { showToast } from "@/shared/lib/toast";
import { setCustomerAuth } from "@/shared/redux/reducers/authSlice";

import { App_Url_New, LOB_IDS, type LobId } from "@/shared/common/static";
import { fetchResumeSession } from "@/shared/lib/fetchResumeSession";
import { dispatchResumeJourney } from "@/shared/utils/dispatchResumeJourney";
import {
  buildResumeTargetPath,
  getClientIdFromVerifyResponse,
  type ResumeSessionResponse,
  type ResumeTarget,
} from "../../utils/resumeJourney";
import LoginModal from "./LoginModal";
import UserLoginForm from "./UserLoginForm";
const MAX_INVALID_OTP_ATTEMPTS = 3;

type OtpApiError = { O; message?: string; detail?: string };

type MessageType = "success" | "error" | "info";

type OtpResumeContext = {
  target: ResumeTarget;
  clientId: string;
  verifyResponse: unknown;
};

type OtpResumeLoginProps = {
  isOpen: boolean;
  onClose: () => void;
  showUserLoginForm?: boolean;
  emailAddress?: string;
  phoneNumber?: string;
  lobId?: number;
  fallbackClientId?: string | null;
  initialMessage?: string;
  title?: string;
  validationType?: "email" | "phone" | "auto";
  autoRedirect?: boolean;
  renderInline?: boolean;
  onSessionResolved?: (
    sessionData: ResumeSessionResponse,
    context: OtpResumeContext
  ) => void | Promise<void>;
};

const buildOtpBody = (identifier: string, type: "email" | "phone", otp?: string) => {
  if (type === "email") {
    return otp ? { email: identifier, otp } : { email: identifier };
  }

  const payload = {
    phone_number: identifier,
    mobile_number: identifier,
    phone: identifier,
  };

  if (!otp) return payload;
  return { ...payload, otp };
};

const resolveLobIdFromPayload = (payload: unknown): LobId | null => {
  if (!payload || typeof payload !== "object") return null;

  const data = payload as {
    lob_id?: number | string;
    session_data?: { lob_id?: number | string };
    session?: { lob_id?: number | string };
    quote_session?: { lob_id?: number | string };
  };

  const candidate =
    data.lob_id ?? data.session_data?.lob_id ?? data.session?.lob_id ?? data.quote_session?.lob_id;
  const lobId = Number(candidate);

  if (
    lobId === LOB_IDS.TRAVEL ||
    lobId === LOB_IDS.HEALTH ||
    lobId === LOB_IDS.MOTOR ||
    lobId === LOB_IDS.LIFE
  ) {
    return lobId;
  }
  return null;
};

const resolveLobIdFromPath = (pathname: string): LobId | null => {
  const lowerPath = pathname.toLowerCase();
  if (lowerPath.includes("/travel")) return LOB_IDS.TRAVEL;
  if (lowerPath.includes("/health")) return LOB_IDS.HEALTH;
  if (lowerPath.includes("/motor") || lowerPath.includes("/car")) return LOB_IDS.MOTOR;
  if (lowerPath.includes("/life")) return LOB_IDS.LIFE;
  return null;
};

const normalizeLobId = (value?: number | null): LobId | null => {
  if (
    value === LOB_IDS.TRAVEL ||
    value === LOB_IDS.HEALTH ||
    value === LOB_IDS.MOTOR ||
    value === LOB_IDS.LIFE
  ) {
    return value;
  }
  return null;
};

const OtpResumeLogin = ({
  isOpen,
  onClose,
  showUserLoginForm = false,
  emailAddress,
  phoneNumber,
  fallbackClientId,
  initialMessage,
  title,
  validationType = "auto",
  autoRedirect = true,
  renderInline = false,
  lobId,
  onSessionResolved,
}: OtpResumeLoginProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(initialMessage || "");
  const [messageType, setMessageType] = useState<MessageType>("info");
  const [invalidOtpAttempts, setInvalidOtpAttempts] = useState(0);

  const resetMessageState = useCallback(() => {
    setMessage(initialMessage || "");
    setMessageType("info");
    setInvalidOtpAttempts(0);
  }, [initialMessage]);

  useEffect(() => {
    if (!isOpen) return;
    resetMessageState();
  }, [isOpen, resetMessageState]);

  const getApiErrorMessage = useCallback((error: unknown, fallback: string) => {
    const axiosError = error as AxiosError<OtpApiError>;
    return (
      axiosError.response?.data?.detail ||
      axiosError.response?.data?.message ||
      (error instanceof Error ? error.message : fallback)
    );
  }, []);

  const handleSendOtp = useCallback(
    async (identifier: string, type: "email" | "phone") => {
      setIsLoading(true);
      setMessage("");
      setMessageType("info");
      setInvalidOtpAttempts(0);

      try {
        const response = await axiosInstance.post(
          App_Url_New.ApiEndPoint.sendOtp,
          buildOtpBody(identifier, type)
        );

        if (response.status === 200) {
          const successMessage =
            response.data?.message ||
            (type === "email" ? "OTP sent to your email." : "OTP sent to your phone number.");
          setMessage(successMessage);
          setMessageType("success");
          showToast(successMessage, "success");
          return true;
        }

        setMessage("Failed to send OTP.");
        setMessageType("error");
        return false;
      } catch (error: unknown) {
        setMessage(getApiErrorMessage(error, "Failed to send OTP."));
        setMessageType("error");
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [getApiErrorMessage]
  );

  const handleVerifyOtp = useCallback(
    async (identifier: string, otp: string, type: "email" | "phone") => {
      setIsLoading(true);
      setMessage("");

      try {
        const verifyResponse = await axiosInstance.post(
          App_Url_New.ApiEndPoint.verifyOtp,
          buildOtpBody(identifier, type, otp)
        );

        if (verifyResponse?.status === 200) {
          dispatch(setCustomerAuth(verifyResponse.data));
        }

        const accessToken = verifyResponse.data?.access_token;
        const clientId = getClientIdFromVerifyResponse(verifyResponse.data, fallbackClientId);

        if (!clientId) {
          throw new Error("Client id not found for existing journey.");
        }

        const resolvedLobId =
          normalizeLobId(lobId) ||
          resolveLobIdFromPayload(verifyResponse.data) ||
          resolveLobIdFromPath(pathname);

        let fullName = "";
        let sessionData: ResumeSessionResponse | null = null;
        let target: ResumeTarget | null = null;
        console.log("BEfore LOB");
        if (resolvedLobId) {
          try {
            sessionData = await fetchResumeSession(clientId, accessToken, resolvedLobId);
            const { resumeState } = dispatchResumeJourney(dispatch, sessionData);
            target = resumeState.target;
          } catch (err) {
            if (err?.response?.status === 404) {
              onClose();
              console.log("Resume session not found. Continuing login.");
              sessionData = null;
              target = null;
            }
          }
        }

        const welcomeMessage = fullName
          ? `Welcome back, ${fullName}. You have signed in successfully.`
          : "You have signed in successfully.";
        showToast(welcomeMessage, "success");
        setMessage(welcomeMessage);
        setMessageType("success");
        setInvalidOtpAttempts(0);

        await new Promise((resolve) => setTimeout(resolve, 900));

        if (sessionData && onSessionResolved && target) {
          await onSessionResolved(sessionData, {
            target,
            clientId,
            verifyResponse: verifyResponse.data,
          });
        } else if (showUserLoginForm) {
          onClose();
          navigate(App_Url_New.D2CPageUrl.retailDashboard);
        } else if (autoRedirect && sessionData && target) {
          navigate(buildResumeTargetPath(target));
        } else if (autoRedirect) {
          navigate(App_Url_New.D2CPageUrl.retailDashboard);
        }
      } catch (error: unknown) {
        const errorMessage = getApiErrorMessage(error, "Unable to verify OTP.");
        const isInvalidOtp = errorMessage.toLowerCase() === "invalid otp";
        const nextInvalidAttemptCount = isInvalidOtp ? invalidOtpAttempts + 1 : invalidOtpAttempts;

        if (isInvalidOtp) {
          setInvalidOtpAttempts(nextInvalidAttemptCount);
        }

        if (isInvalidOtp && nextInvalidAttemptCount >= MAX_INVALID_OTP_ATTEMPTS) {
          const lockedOutMessage =
            "Too many invalid OTP attempts. Redirecting you to the home page.";
          setMessage(lockedOutMessage);
          setMessageType("error");
          showToast(lockedOutMessage, "error");
          onClose();
          await new Promise((resolve) => setTimeout(resolve, 700));
          navigate(App_Url_New.D2CPageUrl.home, { replace: true });
        } else {
          const attemptsRemaining = isInvalidOtp
            ? MAX_INVALID_OTP_ATTEMPTS - nextInvalidAttemptCount
            : null;
          const finalErrorMessage =
            isInvalidOtp && attemptsRemaining !== null
              ? `${errorMessage}. ${attemptsRemaining} attempt${
                  attemptsRemaining === 1 ? "" : "s"
                } remaining.`
              : errorMessage;

          setMessage(finalErrorMessage);
          setMessageType("error");
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      autoRedirect,
      dispatch,
      fallbackClientId,
      getApiErrorMessage,
      invalidOtpAttempts,
      lobId,
      navigate,
      onClose,
      onSessionResolved,
      pathname,
      showUserLoginForm,
    ]
  );

  const modalProps = useMemo(
    () => ({
      isOpen,
      onClose,
      onSendOtp: handleSendOtp,
      onVerifyOtp: handleVerifyOtp,
      isLoading,
    }),
    [handleSendOtp, handleVerifyOtp, isLoading, isOpen, onClose]
  );

  if (showUserLoginForm) {
    if (!isOpen) return null;

    if (renderInline) {
      return (
        <UserLoginForm
          {...modalProps}
          isOpen={true}
          errorMessage={messageType === "error" ? message : ""}
        />
      );
    }

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-theme-text/50 backdrop-blur-sm" onClick={onClose} />
        <div className="relative w-full max-w-lg mx-4">
          <UserLoginForm
            {...modalProps}
            isOpen={true}
            errorMessage={messageType === "error" ? message : ""}
          />
        </div>
      </div>
    );
  }

  return (
    <LoginModal
      {...modalProps}
      message={message}
      messageType={messageType}
      title={title}
      validationType={validationType}
      emailAddress={emailAddress}
      phoneNumber={phoneNumber}
      renderInline={renderInline}
    />
  );
};

export type { OtpResumeContext, OtpResumeLoginProps };
export default OtpResumeLogin;
